import * as THREE from 'three'

import {
  parseMdl,
  sampleBoneLocals,
  findSequence,
  type Mdl,
} from '@/cs/mdl'
import { buildMdlMesh, computeBoneWorld, type MdlBuilt } from '@/cs/buildMdlMesh'
import { parseSpr } from '@/cs/spr'
import type { WeaponKind } from '@/net/protocol'
export type { WeaponKind } from '@/net/protocol'

const MUZZLE_URL = '/cs/sprites/muzzleflash1.spr'
const KNIFE_SWING_MISS_INTERVAL = 0.35
const KNIFE_SWING_HIT_INTERVAL = 0.4
const KNIFE_STAB_MISS_INTERVAL = 1.0
const KNIFE_STAB_HIT_INTERVAL = 1.1

export interface WeaponConfig {
  name: string
  modelUrl: string
  /** Melee weapons have no ammo, no reload and no muzzle flash. */
  melee: boolean
  clipSize: number
  reserve: number
  fireInterval: number
  reloadStyle?: 'magazine' | 'shotgun'
  /** Base transform placing the GoldSrc view model in the overlay camera. */
  scale: number
  pos: [number, number, number]
}

export const AK47_CONFIG: WeaponConfig = {
  name: 'AK-47',
  modelUrl: '/cs/models/v_ak47.mdl',
  melee: false,
  clipSize: 30,
  reserve: 90,
  fireInterval: 0.0955, // real CS AK-47 cycle time (ReGameDLL)
  scale: 0.04,
  pos: [0.16, -0.1, -0.3],
}

export const KNIFE_CONFIG: WeaponConfig = {
  name: 'KNIFE',
  modelUrl: '/cs/models/v_knife.mdl',
  melee: true,
  clipSize: 0,
  reserve: 0,
  fireInterval: KNIFE_SWING_HIT_INTERVAL, // CS knife swing hit cadence
  // The knife model is smaller and sits differently than the rifle, so it gets
  // its own placement (held in the right hand, blade up).
  scale: 0.05,
  pos: [0.0, 0.03, -0.35],
}

export const WEAPON_CONFIGS: Record<WeaponKind, WeaponConfig> = {
  knife: KNIFE_CONFIG,
  ak: AK47_CONFIG,
  mp5: {
    name: 'MP5 NAVY',
    modelUrl: '/cs/models/v_mp5.mdl',
    melee: false,
    clipSize: 30,
    reserve: 120,
    fireInterval: 0.08,
    scale: 0.04,
    pos: [0.14, -0.1, -0.3],
  },
  tmp: {
    name: 'TMP',
    modelUrl: '/cs/models/v_tmp.mdl',
    melee: false,
    clipSize: 30,
    reserve: 120,
    fireInterval: 0.07,
    scale: 0.04,
    pos: [0.14, -0.1, -0.3],
  },
  p90: {
    name: 'P90',
    modelUrl: '/cs/models/v_p90.mdl',
    melee: false,
    clipSize: 50,
    reserve: 100,
    fireInterval: 0.07,
    scale: 0.04,
    pos: [0.14, -0.1, -0.3],
  },
  mac10: {
    name: 'MAC-10',
    modelUrl: '/cs/models/v_mac10.mdl',
    melee: false,
    clipSize: 30,
    reserve: 100,
    fireInterval: 0.075,
    scale: 0.04,
    pos: [0.14, -0.1, -0.3],
  },
  m4a1: {
    name: 'M4A1',
    modelUrl: '/cs/models/v_m4a1.mdl',
    melee: false,
    clipSize: 30,
    reserve: 90,
    fireInterval: 0.09,
    scale: 0.04,
    pos: [0.15, -0.1, -0.3],
  },
  m3: {
    name: 'M3',
    modelUrl: '/cs/models/v_m3.mdl',
    melee: false,
    clipSize: 8,
    reserve: 32,
    fireInterval: 0.88,
    reloadStyle: 'shotgun',
    scale: 0.04,
    pos: [0.15, -0.1, -0.3],
  },
  xm1014: {
    name: 'XM1014',
    modelUrl: '/cs/models/v_xm1014.mdl',
    melee: false,
    clipSize: 7,
    reserve: 32,
    fireInterval: 0.25,
    reloadStyle: 'shotgun',
    scale: 0.04,
    pos: [0.15, -0.1, -0.3],
  },
  m249: {
    name: 'M249',
    modelUrl: '/cs/models/v_m249.mdl',
    melee: false,
    clipSize: 100,
    reserve: 200,
    fireInterval: 0.08,
    scale: 0.04,
    pos: [0.16, -0.1, -0.3],
  },
  glock: {
    name: 'GLOCK18',
    modelUrl: '/cs/models/v_glock18.mdl',
    melee: false,
    clipSize: 20,
    reserve: 120,
    fireInterval: 0.15,
    scale: 0.04,
    pos: [0.12, -0.1, -0.32],
  },
  usp: {
    name: 'USP',
    modelUrl: '/cs/models/v_usp.mdl',
    melee: false,
    clipSize: 12,
    reserve: 100,
    fireInterval: 0.15,
    scale: 0.04,
    pos: [0.12, -0.1, -0.32],
  },
  deagle: {
    name: 'DEAGLE',
    modelUrl: '/cs/models/v_deagle.mdl',
    melee: false,
    clipSize: 7,
    reserve: 35,
    fireInterval: 0.22,
    scale: 0.04,
    pos: [0.12, -0.1, -0.32],
  },
  p228: {
    name: 'P228',
    modelUrl: '/cs/models/v_p228.mdl',
    melee: false,
    clipSize: 13,
    reserve: 52,
    fireInterval: 0.15,
    scale: 0.04,
    pos: [0.12, -0.1, -0.32],
  },
  elite: {
    name: 'ELITE',
    modelUrl: '/cs/models/v_elite.mdl',
    melee: false,
    clipSize: 30,
    reserve: 120,
    fireInterval: 0.12,
    scale: 0.04,
    pos: [0.12, -0.1, -0.32],
  },
  fiveseven: {
    name: 'FIVE-SEVEN',
    modelUrl: '/cs/models/v_fiveseven.mdl',
    melee: false,
    clipSize: 20,
    reserve: 100,
    fireInterval: 0.15,
    scale: 0.04,
    pos: [0.12, -0.1, -0.32],
  },
}

// GoldSrc models face +X → rotate to point down -Z (into the screen).
const VM_ROT = new THREE.Euler(0, Math.PI / 2, 0)

/**
 * First-person view model: a real CS 1.6 `v_*.mdl` (hands + weapon) rendered in
 * its own overlay scene/camera so it is never clipped or occluded. The skeleton
 * is animated every frame — idle sway, the draw on equip and the real reload
 * sequence — rather than frozen on a single frame.
 */
export default class Weapon {
  public readonly scene = new THREE.Scene()
  public readonly camera: THREE.PerspectiveCamera
  public readonly name: string
  public readonly melee: boolean

  public clipSize: number
  public ammo: number
  public reserve: number
  public lastFireDuration = 0
  public lastFireAnimDuration = 0

  private readonly group = new THREE.Group()
  private muzzle?: THREE.Mesh
  private viewMesh?: THREE.Object3D

  private readonly fireInterval: number
  private cooldown = 0
  private muzzleTimer = 0
  private recoilZ = 0
  private recoilPitch = 0
  private bobTime = 0
  private bobSpeed = 0

  // Skeletal animation state.
  private mdl?: Mdl
  private built?: MdlBuilt
  private seqIdle = 0
  private seqDraw = 0
  private seqReload = 0
  private seqShoot: number[] = []
  private shootAlt = 0
  private seqShotgunStart = 0
  private seqShotgunInsert = 0
  private seqShotgunAfter = 0
  private seqSlash: number[] = [] // melee primary swings (alternated)
  private seqStab = 0 // melee secondary hit
  private seqStabMiss = 0 // melee secondary miss
  private slashAlt = 0
  private animSeq = -1
  private animTime = 0
  private animLoop = true
  private animDuration = -1 // >0: play the whole sequence over this many seconds

  public reloading = false
  private reloadDuration = 2.4
  private reloadTime = 0
  private reloadRefilled = false
  private shotgunPhase: 'none' | 'start' | 'insert' | 'after' = 'none'
  private readonly scratch = new THREE.Vector3()
  private readonly muzzleProj = new THREE.Vector3()

  /** Reload progress 0..1 (drives the third-person reload pose). */
  public get reloadProgress(): number {
    return this.reloading && this.reloadDuration > 0
      ? Math.min(1, this.reloadTime / this.reloadDuration)
      : 0
  }

  constructor(private readonly config: WeaponConfig = AK47_CONFIG) {
    this.name = config.name
    this.melee = config.melee
    this.clipSize = config.clipSize
    this.ammo = config.clipSize
    this.reserve = config.reserve
    this.fireInterval = config.fireInterval
    this.lastFireDuration = config.fireInterval
    this.lastFireAnimDuration = config.fireInterval

    this.camera = new THREE.PerspectiveCamera(54, 1, 0.01, 100)

    this.scene.add(new THREE.AmbientLight(0xffffff, 2.8))
    const key = new THREE.DirectionalLight(0xfff2dc, 3.0)
    key.position.set(-0.6, 1, 0.9)
    this.scene.add(key)
    const fill = new THREE.DirectionalLight(0xc8ddff, 1.0)
    fill.position.set(0.45, 0.25, 0.75)
    this.scene.add(fill)
    const rim = new THREE.DirectionalLight(0x88aaff, 1.15)
    rim.position.set(0.8, 0.2, -0.6)
    this.scene.add(rim)

    if (!config.melee) {
      this.muzzle = this.buildMuzzle()
      void this.loadMuzzleSprite()
    }
    this.scene.add(this.group)
    void this.loadViewmodel()
  }

  private buildMuzzle(): THREE.Mesh {
    const muzzle = new THREE.Mesh(
      new THREE.PlaneGeometry(0.2, 0.2),
      new THREE.MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        depthTest: false, // muzzle flash always draws over the gun
        side: THREE.DoubleSide,
      }),
    )
    muzzle.position.set(0.0, -0.22, -1.45) // at the v_ak47 barrel tip (until skinned)
    muzzle.visible = false
    this.group.add(muzzle)
    return muzzle
  }

  /** Use the original GoldSrc muzzle-flash sprite. */
  private async loadMuzzleSprite(): Promise<void> {
    if (!this.muzzle) return
    try {
      const res = await fetch(MUZZLE_URL)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const spr = parseSpr(await res.arrayBuffer())
      const tex = new THREE.DataTexture(
        spr.rgba,
        spr.width,
        spr.height,
        THREE.RGBAFormat,
      )
      tex.colorSpace = THREE.SRGBColorSpace
      tex.needsUpdate = true
      const mat = this.muzzle.material as THREE.MeshBasicMaterial
      mat.map = tex
      mat.needsUpdate = true
    } catch (error) {
      console.warn('[cs] muzzle sprite failed to load:', error)
    }
  }

  private async loadViewmodel(): Promise<void> {
    try {
      const res = await fetch(this.config.modelUrl)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const mdl = parseMdl(await res.arrayBuffer())
      const built = buildMdlMesh(mdl)

      this.seqIdle = findSequence(mdl, 'idle', 'ref')
      this.seqDraw = findSequence(mdl, 'draw', 'deploy', 'idle')
      this.seqReload = findSequence(mdl, 'reload')
      this.seqShoot = mdl.sequences
        .map((seq, i) => ({ label: seq.label.toLowerCase(), i }))
        .filter((seq) => seq.label.startsWith('shoot') || seq.label === 'fire')
        .map((seq) => seq.i)
      if (this.seqShoot.length === 0)
        this.seqShoot = [findSequence(mdl, 'shoot', 'fire', 'idle')]
      if (this.config.reloadStyle === 'shotgun') {
        this.seqShotgunStart = findSequence(mdl, 'start_reload', 'reload')
        this.seqShotgunInsert = findSequence(mdl, 'insert', 'reload')
        this.seqShotgunAfter = findSequence(mdl, 'after_reload', 'pump', 'idle')
      }
      const rs = mdl.sequences[this.seqReload]
      if (rs && rs.fps > 0) this.reloadDuration = rs.numFrames / rs.fps
      if (this.melee) {
        // Full-arc swings (alternated) for the primary; a thrust for secondary.
        this.seqSlash = [
          findSequence(mdl, 'midslash1', 'slash1'),
          findSequence(mdl, 'midslash2', 'slash2'),
        ]
        this.seqStab = findSequence(mdl, 'stab', 'midslash1')
        this.seqStabMiss = findSequence(mdl, 'stab_miss', 'stab')
      }

      const mesh = built.group
      mesh.scale.setScalar(this.config.scale)
      mesh.rotation.copy(VM_ROT)
      mesh.position.set(...this.config.pos)
      mesh.updateMatrix()
      this.group.add(mesh)

      this.mdl = mdl
      this.built = built
      this.viewMesh = mesh

      // Play the draw on first load, then settle into the idle loop.
      this.play(this.seqDraw, false)
      console.info(`[cs] view model loaded (${this.config.modelUrl})`)
    } catch (error) {
      console.warn('[cs] view model failed to load:', error)
    }
  }

  private play(seq: number, loop: boolean, duration = -1): void {
    this.animSeq = seq
    this.animTime = 0
    this.animLoop = loop
    this.animDuration = duration
  }

  private sequenceDuration(seq: number): number {
    const s = this.mdl?.sequences[seq]
    if (!s) return this.fireInterval
    const fps = s.fps > 0 ? s.fps : 12
    return s.numFrames / fps
  }

  /** Advance the skeletal animation and re-skin the mesh. */
  private updateAnim(dt: number): void {
    if (!this.mdl || !this.built || this.animSeq < 0) return
    const s = this.mdl.sequences[this.animSeq]
    const fps = s.fps > 0 ? s.fps : 12
    const frames = s.numFrames > 1 ? s.numFrames : 1
    const useDur = this.animDuration > 0

    this.animTime += dt
    let frame = useDur
      ? Math.min(1, this.animTime / this.animDuration) * (frames - 1)
      : this.animTime * fps
    const ended = useDur ? this.animTime >= this.animDuration : frame >= frames - 1
    if (ended) {
      if (this.animLoop && !useDur) {
        frame %= frames
      } else {
        // A one-shot animation finished → fall back to the idle loop.
        this.play(this.seqIdle, true)
        frame = 0
      }
    }

    const locals = sampleBoneLocals(this.mdl, this.animSeq, frame)
    const boneWorld = computeBoneWorld(this.mdl, locals)
    this.built.skin(boneWorld)
    this.placeMuzzle(boneWorld)
  }

  /** Keep the muzzle flash glued to attachment 0 (the barrel) as bones move. */
  private placeMuzzle(boneWorld: THREE.Matrix4[]): void {
    if (!this.muzzle || !this.viewMesh || !this.mdl) return
    const att = this.mdl.attachments[0]
    if (!att) return
    this.scratch
      .set(att.org[0], att.org[1], att.org[2])
      .applyMatrix4(boneWorld[att.bone])
    this.muzzle.position
      .set(this.scratch.x, this.scratch.z, -this.scratch.y) // GoldSrc → three
      .applyMatrix4(this.viewMesh.matrix) // into holder space
  }

  /** The muzzle tip's normalized screen position (NDC x,y in [-1, 1]) as it
   * appears in the overlay, or false for melee / before the model loads. Lets the
   * world-space tracer start exactly where the on-screen muzzle flash is. */
  public muzzleNdc(out: THREE.Vector2): boolean {
    if (this.melee || !this.muzzle) return false
    this.muzzle.updateWorldMatrix(true, false)
    this.muzzleProj
      .setFromMatrixPosition(this.muzzle.matrixWorld)
      .project(this.camera)
    if (!Number.isFinite(this.muzzleProj.x) || !Number.isFinite(this.muzzleProj.y))
      return false
    out.set(this.muzzleProj.x, this.muzzleProj.y)
    return true
  }

  /** Re-arm and play the draw animation (when switching to this weapon). */
  public equip(): void {
    this.cooldown = 0.3
    this.reloading = false
    this.reloadTime = 0
    if (this.mdl) this.play(this.seqDraw, false)
  }

  /**
   * Attempt to attack this frame. `secondary` is the right mouse button (knife
   * stab; ignored by the gun). Returns true if a round/slash actually went off.
   */
  public tryFire(secondary = false, hit = false): boolean {
    if (this.cooldown > 0 || this.reloading) return false

    if (this.melee) {
      if (secondary) {
        this.cooldown = hit
          ? KNIFE_STAB_HIT_INTERVAL
          : KNIFE_STAB_MISS_INTERVAL
        this.lastFireDuration = this.cooldown
        const seq = hit ? this.seqStab : this.seqStabMiss
        this.lastFireAnimDuration = this.sequenceDuration(seq)
        if (this.mdl) this.play(seq, false)
      } else {
        this.cooldown = hit
          ? KNIFE_SWING_HIT_INTERVAL
          : KNIFE_SWING_MISS_INTERVAL
        this.lastFireDuration = this.cooldown
        const seq = this.seqSlash[this.slashAlt % this.seqSlash.length]
        this.slashAlt++
        this.lastFireAnimDuration = this.sequenceDuration(seq ?? this.seqIdle)
        if (this.mdl) this.play(seq ?? this.seqIdle, false)
      }
      return true
    }

    if (secondary || this.ammo <= 0) return false // AK has no secondary fire
    this.cooldown = this.fireInterval
    this.lastFireDuration = this.fireInterval
    this.lastFireAnimDuration = this.fireInterval
    this.ammo -= 1
    const seq = this.seqShoot[this.shootAlt % this.seqShoot.length]
    this.shootAlt++
    if (this.mdl) {
      this.lastFireAnimDuration = this.sequenceDuration(seq)
      this.play(seq, false)
    }
    this.muzzleTimer = 0.045
    if (this.muzzle) {
      this.muzzle.rotation.z = Math.random() * Math.PI
      this.muzzle.scale.setScalar(0.55 + Math.random() * 0.4)
      this.muzzle.visible = true
    }
    this.recoilZ = Math.min(this.recoilZ + 0.05, 0.12)
    this.recoilPitch = Math.min(this.recoilPitch + 0.05, 0.12)
    return true
  }

  /** Returns true if a reload actually started (for triggering the sound). */
  public reload(): boolean {
    if (this.melee) return false
    if (this.reloading || this.ammo >= this.clipSize || this.reserve <= 0)
      return false
    this.reloading = true
    this.reloadTime = 0
    this.reloadRefilled = false
    if (this.config.reloadStyle === 'shotgun') {
      this.shotgunPhase = 'start'
      this.play(this.seqShotgunStart, false)
      this.reloadDuration =
        this.sequenceDuration(this.seqShotgunStart) +
        Math.max(1, this.clipSize - this.ammo) *
          this.sequenceDuration(this.seqShotgunInsert) +
        this.sequenceDuration(this.seqShotgunAfter)
    } else {
      this.play(this.seqReload, false)
    }
    return true
  }

  /** @param speed01 horizontal speed normalised to 0..1, for view-bob. */
  public update(dt: number, speed01: number): void {
    if (this.cooldown > 0) this.cooldown -= dt

    if (this.muzzleTimer > 0) {
      this.muzzleTimer -= dt
      if (this.muzzleTimer <= 0 && this.muzzle) this.muzzle.visible = false
    }

    // Recoil recovery.
    this.recoilZ *= Math.exp(-dt * 14)
    this.recoilPitch *= Math.exp(-dt * 14)

    // Weapon bob: subtle and smooth. Avoid abs(sin), which creates a sharp
    // floor-bounce feel when walking.
    this.bobSpeed += (speed01 - this.bobSpeed) * (1 - Math.exp(-dt * 10))
    this.bobTime += dt * (2.8 + this.bobSpeed * 4.8)
    const bobAmp = this.bobSpeed * 0.006
    const bobX = Math.cos(this.bobTime) * bobAmp
    const bobY = Math.sin(this.bobTime) * bobAmp * 0.45

    // Reload: magazines refill once; shotguns insert shells one by one using the
    // model's real start/insert/after_reload sequence chain.
    if (this.reloading) {
      this.reloadTime += dt
      if (this.config.reloadStyle === 'shotgun') {
        this.updateShotgunReload()
      } else {
        const p = this.reloadProgress
        if (!this.reloadRefilled && p >= 0.6) {
          const take = Math.min(this.clipSize - this.ammo, this.reserve)
          this.ammo += take
          this.reserve -= take
          this.reloadRefilled = true
        }
        if (p >= 1) this.reloading = false
      }
    }

    // Skeletal pose (idle sway / draw / reload), then bob + recoil on the holder.
    this.updateAnim(dt)
    this.group.position.set(bobX, bobY, this.recoilZ)
    this.group.rotation.x = -this.recoilPitch
  }

  public resize(aspect: number): void {
    this.camera.aspect = aspect
    this.camera.updateProjectionMatrix()
  }

  private updateShotgunReload(): void {
    if (!this.mdl) return
    const phaseSeq =
      this.shotgunPhase === 'start'
        ? this.seqShotgunStart
        : this.shotgunPhase === 'insert'
          ? this.seqShotgunInsert
          : this.seqShotgunAfter
    const phaseDuration = Math.max(0.05, this.sequenceDuration(phaseSeq))
    const p = this.reloadTime / phaseDuration

    if (
      this.shotgunPhase === 'insert' &&
      !this.reloadRefilled &&
      p >= 0.55
    ) {
      this.ammo += 1
      this.reserve -= 1
      this.reloadRefilled = true
    }
    if (p < 1) return

    this.reloadTime = 0
    this.reloadRefilled = false
    if (this.shotgunPhase === 'start') {
      this.shotgunPhase = 'insert'
      this.play(this.seqShotgunInsert, false)
    } else if (
      this.shotgunPhase === 'insert' &&
      this.ammo < this.clipSize &&
      this.reserve > 0
    ) {
      this.play(this.seqShotgunInsert, false)
    } else {
      if (this.shotgunPhase !== 'after') {
        this.shotgunPhase = 'after'
        this.play(this.seqShotgunAfter, false)
      } else {
        this.shotgunPhase = 'none'
        this.reloading = false
      }
    }
  }
}
